// Tryb — cover chooser.
//
// A facilitator either uploads a photo or builds a cover from a template:
// a colour, an icon drawn from the category, and whether the experience's name sits
// on it. Template covers re-render from the name, so renaming the experience updates
// the cover without anyone touching it again.

import { useMemo, useState } from "react";
import { ENUMS } from "@shared/fields.js";
import { Button, Icon, Input, Tag } from "../ds/index.js";
import { COVER_COLOURS, iconForCategory } from "../lib/useFieldViews.js";
import { useStudio } from "../lib/studioContext.js";
import { api } from "../lib/api.js";
import CanvaPanel from "./CanvaPanel.jsx";

const CATEGORIES = [
  "Featured",
  "Movement & fitness",
  "Food & drink",
  "Craft & making",
  "Music",
  "Reading & writing",
  "Games & puzzles",
  "Outdoors & nature",
  "Learning & skills",
  "Art & culture",
  "Wellbeing",
  "Quiet",
];

const STYLES = [
  ["both", "Name + icon"],
  ["type", "Name only"],
  ["icon", "Icon only"],
];

const FONTS = [
  ["display", "Display"],
  ["mono", "Mono"],
];

const railLabel = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

export default function CoverChooser({ onClose }) {
  const { vals, setField, showToast, refresh } = useStudio();
  const current = vals["B1.07"] && typeof vals["B1.07"] === "object" ? vals["B1.07"] : null;

  // "templates" is the built-in picker; "canva" hands over to the facilitator's
  // own Canva account for a properly designed thumbnail.
  const [tab, setTab] = useState("templates");

  const [draft, setDraft] = useState({
    color: current?.color || "ember",
    style: current?.style || "both",
    font: current?.font || "display",
    icon: current?.icon || iconForCategory(vals["B1.04"] || "Featured"),
    cat: vals["B1.04"] || "Featured",
    query: "",
  });

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));

  const q = draft.query.trim().toLowerCase();
  const cats = useMemo(
    () =>
      q
        ? CATEGORIES.filter(
            (n) => n.toLowerCase().includes(q) || (ENUMS.SUBCATEGORY[n] || []).some((x) => x.toLowerCase().includes(q)),
          )
        : CATEGORIES,
    [q],
  );

  const activeCat = cats.includes(draft.cat) ? draft.cat : cats[0] || "Featured";
  const text = vals["B1.01"] || "Your tryb";
  const icon = activeCat === "Featured" ? iconForCategory(vals["B1.04"] || "") : iconForCategory(activeCat);

  // One tile per colour × style, skipping icon-only in Featured so the
  // opening grid leads with covers that carry the name.
  const tiles = useMemo(() => {
    const out = [];
    Object.keys(COVER_COLOURS).forEach((ck, i) => {
      ["both", "type", "icon"].forEach((sk, j) => {
        if (activeCat === "Featured" && j === 2) return;
        const fk = (i + j) % 2 === 0 ? "display" : "mono";
        out.push({
          key: `${ck}-${sk}-${fk}`,
          bg: COVER_COLOURS[ck].bg,
          fg: COVER_COLOURS[ck].fg,
          icon,
          text,
          showIcon: sk !== "type",
          showText: sk !== "icon",
          font: fk === "mono" ? "var(--font-mono)" : "var(--font-display)",
          selected: draft.color === ck && draft.style === sk && draft.font === fk && draft.icon === icon,
          pick: () => patch({ color: ck, style: sk, font: fk, icon }),
        });
      });
    });
    return out;
  }, [activeCat, draft.color, draft.style, draft.font, draft.icon, icon, text]);

  const useTemplate = () => {
    setField("B1.07", { kind: "template", color: draft.color, style: draft.style, font: draft.font, icon: draft.icon, text });
    onClose();
    showToast("Cover set", "It updates itself if you rename the tryb.");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return showToast("That needs to be a photo", "JPG, PNG or HEIC.");

    try {
      const { answer } = await api.uploadDocument("B1.07", file);
      setField("B1.07", answer);
      await refresh();
      onClose();
      showToast("Cover uploaded", "Remember: real faces only with their okay.");
    } catch (err) {
      showToast(err.message, err.hint);
    }
  };

  return (
    <div
      data-screen-label="Cover chooser"
      style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "color-mix(in srgb, var(--ink-950) 46%, transparent)", backdropFilter: "blur(3px)" }}
      />
      <div
        style={{
          position: "relative",
          width: 980,
          maxWidth: "100%",
          height: "88vh",
          background: "var(--surface-card)",
          borderRadius: "var(--radius-2xl)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "trybRise var(--dur-slow) var(--ease-spring)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 28px 0" }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "var(--text-xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)" }}>
              Pick a cover
            </h2>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginTop: 2 }}>
              Your own photo, one of ours with your tryb’s name on it, or design one in Canva.
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        <div style={{ display: "flex", gap: 4, padding: "16px 28px 0", borderBottom: "1px solid var(--border-subtle)" }}>
          {[
            ["templates", "Upload or template"],
            ["canva", "Design in Canva"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "8px 14px 12px",
                fontFamily: "var(--font-body)",
                fontSize: "var(--text-sm)",
                fontWeight: tab === k ? 700 : 500,
                color: tab === k ? "var(--text-primary)" : "var(--text-muted)",
                borderBottom: `2px solid ${tab === k ? "var(--ember-500)" : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "canva" && (
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "18px 28px 24px" }}>
            <CanvaPanel onDone={onClose} />
          </div>
        )}

        {tab === "templates" && (
          <>

        <div style={{ padding: "18px 28px 0", display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              position: "relative",
              border: "1px dashed var(--border-strong)",
              borderRadius: "var(--radius-lg)",
              padding: 22,
              textAlign: "center",
              background: "var(--surface-page)",
              cursor: "pointer",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: "var(--text-base)" }}>Drop a photo here, or click to pick one</div>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginTop: 4 }}>
              Landscape works best (3:2). Up to 5MB. Real people only with their okay.
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={onUpload}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
            />
          </div>
          <Input
            placeholder="Search templates — bouldering, supper, quiet…"
            value={draft.query}
            onChange={(e) => patch({ query: e.target.value })}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 20, padding: "18px 28px", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
            {cats.map((n) => (
              <button
                key={n}
                onClick={() => patch({ cat: n, icon: iconForCategory(n) })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  padding: "9px 12px",
                  borderRadius: "var(--radius-sm)",
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-sm)",
                  background: n === activeCat ? "var(--sand-200)" : "transparent",
                  color: "var(--text-primary)",
                  fontWeight: n === activeCat ? 700 : 500,
                }}
              >
                {n}
              </button>
            ))}
          </div>

          <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, paddingRight: 4 }}>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>{activeCat === "Featured" ? "A few we like" : activeCat}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {tiles.map((t) => (
                <button
                  key={t.key}
                  onClick={t.pick}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    aspectRatio: "3 / 2",
                    borderRadius: "var(--radius-md)",
                    position: "relative",
                    overflow: "hidden",
                    background: t.bg,
                    color: t.fg,
                    boxSizing: "border-box",
                    border: `3px solid ${t.selected ? "var(--ember-500)" : "transparent"}`,
                  }}
                >
                  {t.showIcon && (
                    <div style={{ position: "absolute", right: -10, bottom: -14, opacity: 0.22 }}>
                      <Icon name={t.icon} size={90} sw={1.3} />
                    </div>
                  )}
                  {t.showText && (
                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        right: 10,
                        bottom: 9,
                        fontFamily: t.font,
                        fontWeight: 800,
                        fontSize: 13,
                        lineHeight: 1.05,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {t.text}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            padding: "14px 28px",
            display: "flex",
            alignItems: "center",
            gap: 22,
            flexWrap: "wrap",
            background: "var(--surface-page)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={railLabel}>Colour</span>
            {Object.entries(COVER_COLOURS).map(([k, c]) => (
              <button
                key={k}
                onClick={() => patch({ color: k })}
                title={c.label}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  border: `3px solid ${draft.color === k ? "var(--ink-900)" : "var(--border-default)"}`,
                  background: c.bg,
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={railLabel}>Style</span>
            {STYLES.map(([k, l]) => (
              <Tag key={k} selected={draft.style === k} onClick={() => patch({ style: k })}>
                {l}
              </Tag>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={railLabel}>Type</span>
            {FONTS.map(([k, l]) => (
              <Tag key={k} selected={draft.font === k} onClick={() => patch({ font: k })}>
                {l}
              </Tag>
            ))}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={useTemplate}>
              Use this cover
            </Button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
